import type { PinProps } from '@types';
import { AppContext } from 'context/app';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { PinLocation } from '../pinLocation/PinLocation';
import styles from './pin.module.scss';

const Pin = forwardRef<HTMLDivElement, PinProps>(
  ({ location, x, y, side, mapContainerRef }, ref) => {
    const { selectedLocation, setSelectedLocation } = useContext(AppContext);
    const [pinWidth, setPinWidth] = useState(0);

    const iconWidth = 14; // half of 28
    const iconHeight = 19; // half of 38

    const handleShowLocationDetails = useCallback(() => {
      setSelectedLocation(
        selectedLocation?.id !== location.id ? location : undefined,
      );
    }, [selectedLocation, setSelectedLocation, location]);

    useEffect(() => {
      // Assuming the ref is attached to a DOM element, the current property will point to it
      if (ref && typeof ref === 'object' && ref.current) {
        setPinWidth(ref.current.offsetWidth);
      }
    }, [ref]);

    const top = y - iconHeight;
    const left = side === 'left' ? x - pinWidth + iconWidth : x - iconWidth;

    useEffect(() => {
      if (
        ref &&
        typeof ref === 'object' &&
        ref.current &&
        !ref.current.getAttribute('data-bounding-rect-x')
      ) {
        ['x', 'y', 'left', 'right', 'top', 'bottom'].forEach((prop) => {
          ref.current?.setAttribute(
            `data-bounding-rect-${prop}`,
            ref.current
              ?.getBoundingClientRect()
              [prop as keyof DOMRect].toString(),
          );
        });
      }
    }, [ref]);

    return (
      <div
        id={`pin-${location.id}`}
        className={styles.container}
        style={{ top, left, visibility: 'hidden' }}
        ref={ref}
      >
        <PinLocation
          onClick={handleShowLocationDetails}
          location={location}
          active={selectedLocation?.id === location.id}
          side={side}
          mapContainerRef={mapContainerRef}
        />
      </div>
    );
  },
);

export default Pin;
